import React, { useEffect, useState } from 'react';
import {
  Grid, FormControl, TextField, Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import i18n from '@i18n';
import {
  useContacts, useCustomers, usePresupuestos, useStates, useSolicitudes,
} from '@hooks';
import {
  Alert, ButtonAdorned, Subtitle, Select, FileUpload, Autocomplete,
} from '@components/atoms';
import { createPresupuestoSchema } from '@helpers/schemas';
import constants from '@helpers/constants';

const Create = () => {
  const { pages: { employees: { presupuestos: { create: texts } } } } = i18n;
  const history = useHistory();
  const { getContacts, isContactLoading, contactError } = useContacts();
  const { getCustomers, isCustomerLoading, customerError } = useCustomers();
  const { getStates, isStateLoading, stateError } = useStates();
  const { createPresupuesto, isPresupuestoLoading, presupuestoError } = usePresupuestos();
  const { getSolicitudes } = useSolicitudes();

  const [customers, setCustomers] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [states, setStates] = useState([]);

  const { enqueueSnackbar } = useSnackbar();

  const form = useFormik({
    initialValues: {
      customer: '',
      contact: '',
      solicitud: '',
      file: [],
      number: '',
      description: '',
      state: states.length > 0 ? states.find((state) => state.id === constants.STATE.PENDIENTE) : '',
      noSolicitud: false,
    },
    enableReinitialize: true,
    validationSchema: createPresupuestoSchema,
    validateOnChange: true,
    onSubmit: async (values) => {
      const { customer, noSolicitud, ...rest } = values;
      const created = await createPresupuesto(rest);
      if (created) {
        enqueueSnackbar(texts.success, { variant: 'success' });
        history.push('/e/presupuestos');
      }
    },
  });

  useEffect(async () => {
    const serviceStates = await getStates();
    setStates(serviceStates.filter((state) => state.newStateAllowed.employee));
    setCustomers(await getCustomers());
  }, []);

  const onChangeCustomer = async (_, value) => {
    form.setFieldValue('contact', '');
    form.setFieldValue('customer', value);
    setContacts([]);
    setSolicitudes([]);
    if (value && value.id) {
      setContacts(await getContacts(`?customer.id=${value.id}`));
      setSolicitudes(await getSolicitudes(`?contact.customer.id=${value.id}`));
    }
  };

  return (
    <>
      {customerError && <Alert>{customerError.message}</Alert>}
      {contactError && <Alert>{contactError.message}</Alert>}
      {stateError && <Alert>{stateError.message}</Alert>}
      {presupuestoError && <Alert>{presupuestoError.message}</Alert>}
      <Grid container spacing={2}>
        {texts.subtitleData && (
          <Grid item xs={12} align="left">
            <Subtitle>{texts.subtitleData}</Subtitle>
          </Grid>
        )}
        <Grid item xs={6} sm={6} md={8}>
          <FormControl variant="outlined" fullWidth>
            <Autocomplete
              id={(Math.floor(Math.random() * (100 - 0 + 1)) + 0).toLocaleString()}
              label={texts.customer}
              key={(Math.floor(Math.random() * (100 - 0 + 1)) + 0).toLocaleString()}
              defaultValue={form.values.customer}
              onChange={onChangeCustomer}
              error={form.touched.customer && Boolean(form.errors.customer)}
              disabled={isCustomerLoading}
              helperText={form.touched.customer && form.errors.customer && form.errors.customer.id}
              items={customers}
              close
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl variant="outlined" fullWidth>
            <Autocomplete
              key={(Math.floor(Math.random() * (100 - 0 + 1)) + 0).toLocaleString()}
              label="Solicitud"
              defaultValue={form.values.solicitud}
              onChange={(_, value) => form.setFieldValue('solicitud', value)}
              // onChange={form.handleChange}
              error={form.touched.solicitud && Boolean(form.errors.solicitud)}
              disabled={isContactLoading || form.values.noSolicitud}
              helperText={(form.values.customer && contacts.length === 0 && !isContactLoading && texts.noContacts)
                || (form.touched.contact && form.errors.contact && form.errors.contact.id)}
              items={solicitudes.map((values) => ({ name: values.consecutive || 1 }))}
            />
          </FormControl>
        </Grid>
        <Grid item xs={4} sm={6} md={2}>
          <FormControl variant="outlined" fullWidth>
            <FormControlLabel
              control={(
                <Checkbox
                  name="noSolicitud"
                  label={texts.noSolicitud}
                  value={form.values.noSolicitud}
                  onChange={form.handleChange}
                />
          )}
              label={texts.noSolicitud}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl variant="outlined" fullWidth>
            <Autocomplete
              key={(Math.floor(Math.random() * (100 - 0 + 1)) + 0).toLocaleString()}
              label={texts.contact}
              defaultValue={form.values.contact}
              onChange={(_, value) => form.setFieldValue('contact', value)}
              error={form.touched.contact && Boolean(form.errors.contact)}
              disabled={isContactLoading}
              helperText={(form.values.customer && contacts.length === 0 && !isContactLoading && texts.noContacts)
                || (form.touched.contact && form.errors.contact && form.errors.contact.id)}
              items={contacts}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <TextField
              name="number"
              label={texts.number}
              placeholder={texts.numberPlaceholder}
              variant="outlined"
              value={form.values.number}
              onChange={form.handleChange}
              error={form.touched.number && Boolean(form.errors.number)}
              helperText={form.touched.number && form.errors.number}
              disabled={isContactLoading}
              autoComplete="new-number"
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <FormControl fullWidth>
            <TextField
              name="description"
              label={texts.description}
              placeholder={texts.descriptionPlaceholder}
              variant="outlined"
              value={form.values.description}
              onChange={form.handleChange}
              error={form.touched.description && Boolean(form.errors.description)}
              helperText={form.touched.description && form.errors.description}
              disabled={isContactLoading}
              autoComplete="new-description"
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <FormControl fullWidth>
            <FileUpload
              title={texts.file}
              name="file"
              onChange={form.handleChange}
              value={form.values.file}
              error={form.touched.file && Boolean(form.errors.file)}
              helperText={form.touched.file && form.errors.file && form.errors.file}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl variant="outlined" fullWidth>
            <Select
              name="state"
              label={texts.state}
              value={form.values.state}
              onChange={form.handleChange}
              error={form.touched.state && Boolean(form.errors.state)}
              disabled={isStateLoading}
              helperText={form.touched.state && form.errors.state && form.errors.state.id}
              items={states}
            />
          </FormControl>
        </Grid>
        <Grid container spacing={1} justifyContent="flex-end">
          <Grid item>
            <ButtonAdorned color="default" onClick={() => history.push('/e/presupuestos')}>
              {texts.list}
            </ButtonAdorned>
          </Grid>
          <Grid item>
            <form onSubmit={form.handleSubmit}>
              <ButtonAdorned loading={isPresupuestoLoading} disabled={isPresupuestoLoading} type="submit">
                {texts.save}
              </ButtonAdorned>
            </form>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

Create.propTypes = {
};

Create.defaultProps = {
};

export default Create;
